import React, { useState, useEffect, useCallback } from 'react';
import {
  getSchemes,
  submitSchemeReferral,
  getMemberReferrals,
  addGovApplicationMessage,
  uploadReferralDocument,
  daysSince,
} from '../../../api/programmesApi';
import { getMember } from '../../../api/communityApi';

// ── Helpers ────────────────────────────────────────────────────────────────────
const formatDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

function referralStatusBadge(status) {
  const map = {
    referred:    { cls: 'bb-member-badge--gray',  label: 'Referred' },
    in_progress: { cls: 'bb-member-badge--blue',  label: 'In Progress' },
    approved:    { cls: 'bb-member-badge--green', label: 'Approved' },
    rejected:    { cls: 'bb-member-badge--red',   label: 'Rejected' },
  };
  const { cls, label } = map[status] || { cls: 'bb-member-badge--gray', label: status };
  return <span className={`bb-member-badge ${cls}`}>{label}</span>;
}

// ── Helpers ────────────────────────────────────────────────────────────────────
// eligibility is stored as an object {widowStatus[], maxIncome, childrenCount, ageGroups[]}
// but may also be a plain string or array in older records — handle all cases.
const eligToString = (e) => {
  if (!e) return '';
  if (typeof e === 'object' && !Array.isArray(e)) {
    return [
      ...(e.widowStatus  || []),
      e.maxIncome     ? `income ${e.maxIncome}`     : '',
      e.childrenCount ? `children ${e.childrenCount}` : '',
      ...(e.ageGroups || []),
    ].filter(Boolean).join(' ').toLowerCase();
  }
  if (Array.isArray(e)) return e.join(' ').toLowerCase();
  return String(e).toLowerCase();
};

// Human-readable summary of an eligibility object for display
const eligDisplay = (e) => {
  if (!e) return '';
  if (typeof e === 'object' && !Array.isArray(e)) {
    const parts = [];
    if (e.widowStatus?.length)  parts.push(e.widowStatus.join(', '));
    if (e.maxIncome)                                    parts.push(`Income ≤ $${e.maxIncome}/month`);
    if (e.childrenCount && parseInt(e.childrenCount) > 0) parts.push(`Min ${e.childrenCount} dependent child${parseInt(e.childrenCount) === 1 ? '' : 'ren'}`);
    if (e.ageGroups?.length)                            parts.push(`Ages: ${e.ageGroups.join(', ')}`);
    return parts.join(' · ') || '—';
  }
  if (Array.isArray(e)) return e.join(', ');
  return String(e);
};

// ── Eligibility matching ───────────────────────────────────────────────────────
function schemeMatches(scheme, answers) {
  if (!answers.submitted) return null;
  const e = scheme.eligibility;
  let matches = true;

  if (e && typeof e === 'object' && !Array.isArray(e)) {
    // Children: only apply check if scheme has a minimum > 0; fail if member has fewer
    if (e.childrenCount && parseInt(e.childrenCount) > 0) {
      const memberChildren = answers.children === '3+' ? 4 : (parseInt(answers.children) || 0);
      if (memberChildren < parseInt(e.childrenCount)) matches = false;
    }
    // Income: map bracket label to its upper bound and compare against scheme max
    if (e.maxIncome) {
      const incomeMap = { 'Under $1,500': 1499, '$1,500 – $3,000': 3000, 'Over $3,000': 99999 };
      const memberIncome = incomeMap[answers.income] ?? 0;
      if (memberIncome > parseInt(e.maxIncome)) matches = false;
    }
    // Family status: compare directly against stored admin values; 'Any' matches all
    if (e.widowStatus?.length && !e.widowStatus.includes('Any')) {
      if (!e.widowStatus.includes(answers.familyStatus)) matches = false;
    }
    // Age groups: auto-calculated from profile DOB or manually entered
    if (e.ageGroups?.length && answers.age != null) {
      const age = parseInt(answers.age);
      let memberGroup;
      if (age < 18)       memberGroup = 'Under 18';
      else if (age <= 35) memberGroup = '18–35';
      else if (age <= 55) memberGroup = '36–55';
      else                memberGroup = '55+';
      if (!e.ageGroups.includes(memberGroup)) matches = false;
    }
  } else {
    // Fallback: plain string/array heuristics
    const elig = eligToString(e);
    if (elig.includes('unemployed') && answers.employment !== 'Unemployed') matches = false;
  }
  return matches;
}

// ── Card icons (monochromatic dark green, matching Support Options style) ──────
const CARD_ICONS = {
  financial: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
  ),
  mental: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
    </svg>
  ),
  education: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>
    </svg>
  ),
  health: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
    </svg>
  ),
  housing: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/><path d="M9 21V12h6v9"/>
    </svg>
  ),
  employment: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2M12 12v4M10 14h4"/>
    </svg>
  ),
  family: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  food: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/>
      <path d="M16 10a4 4 0 0 1-8 0"/>
    </svg>
  ),
  legal: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  default: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/>
    </svg>
  ),
};

/** Pick a relevant icon by keyword-matching name, category, type and description. */
const getSchemeIcon = (scheme) => {
  const text = [scheme.name, scheme.category, scheme.type, scheme.description, scheme.benefitsSummary]
    .filter(Boolean).join(' ').toLowerCase();
  if (/financ|fund|grant|aid|allowance|hardship|payment|welfare|benefit|income|bursary|cash|relief|subsid|monetary/.test(text)) return CARD_ICONS.financial;
  if (/mental|psycholog|counsel|therapy|therapist|emotional|cognitive|depress|anxiet|wellbeing|grief|bereavement/.test(text)) return CARD_ICONS.mental;
  if (/educat|school|tuition|scholarship|learn|train|skill|course|study|student|academic|literacy|vocat|workshop|certif/.test(text)) return CARD_ICONS.education;
  if (/health|medical|medicine|hospital|clinic|wellness|treatment|rehab|nurs|dental|vision/.test(text)) return CARD_ICONS.health;
  if (/hous|home|shelter|accommodat|rental|rent|property|dwelling|lodg/.test(text)) return CARD_ICONS.housing;
  if (/employ|job|work|career|occupat|workforce|profession|livelihood|placement|enterprise|entrepreneur/.test(text)) return CARD_ICONS.employment;
  if (/child|youth|young|kid|infant|baby|teenager|juvenile|famil|parent|widow/.test(text)) return CARD_ICONS.family;
  if (/food|nutrit|meal|grocery|feed|diet|hunger|nourish|supplement|basket/.test(text)) return CARD_ICONS.food;
  if (/legal|law|lawyer|court|rights|justice|will|estate|document/.test(text)) return CARD_ICONS.legal;
  return CARD_ICONS.default;
};

// ── Explore Schemes Tab ────────────────────────────────────────────────────────
function ExploreSchemes({ orgId, memberId, memberName }) {
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});
  const [submittedIds, setSubmittedIds] = useState(new Set());
  const [toast, setToast] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [data, existing] = await Promise.all([
          getSchemes(orgId),
          getMemberReferrals(orgId, memberId),
        ]);
        setSchemes(data);
        setSubmittedIds(new Set(existing.map((r) => r.schemeId)));
      } catch (err) {
        console.error('Error loading schemes:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [orgId, memberId]);

  const showToast = (msg, isError = false) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(''), 3500);
  };

  const handleReferral = async (scheme) => {
    setSubmitting((prev) => ({ ...prev, [scheme.id]: true }));
    try {
      await submitSchemeReferral(orgId, memberId, memberName, scheme.id, scheme.name);
      setSubmittedIds((prev) => new Set([...prev, scheme.id]));
      showToast(`Referral request submitted for "${scheme.name}".`);
    } catch (err) {
      showToast(err.message || 'Error submitting referral.', true);
    } finally {
      setSubmitting((prev) => ({ ...prev, [scheme.id]: false }));
    }
  };

  const filtered = schemes;

  if (loading) {
    return (
      <div className="bb-empty-state">
        <div className="bb-loading-spinner"></div>
        <p>Loading schemes...</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="bb-member-section-title">Government Schemes</h2>
      <p className="bb-member-section-sub">Browse available support schemes and submit a referral request.</p>

      <div className="bb-member-info-box teal" style={{ marginBottom: 20 }}>
        Submit a referral request to apply for a programme. A caseworker will review your request and
        contact you with next steps.
      </div>

      {filtered.length === 0 ? (
        <div className="bb-empty-state">
          <p>No schemes are currently available.</p>
        </div>
      ) : (
        <div className="bb-member-grid">
          {filtered.map((scheme) => (
            <div key={scheme.id} className="bb-member-card">
              <div style={{ color: '#0F766E', marginBottom: 10, lineHeight: 1 }}>
                {getSchemeIcon(scheme)}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                <div className="bb-member-card-title">{scheme.name}</div>
                <span className="bb-member-badge bb-member-badge--green">Available</span>
              </div>
              <div className="bb-member-card-meta">
                {scheme.administrator && <span>By: {scheme.administrator}</span>}
                {scheme.deadline && <span>Deadline: {formatDate(scheme.deadline)}</span>}
              </div>
              {(scheme.description || scheme.benefitsSummary) && (
                <div className="bb-member-card-desc">{scheme.description || scheme.benefitsSummary}</div>
              )}
              {scheme.eligibility && (
                <div className="bb-member-card-meta" style={{ marginBottom: 8 }}>
                  <span><strong>Eligibility:</strong> {eligDisplay(scheme.eligibility)}</span>
                </div>
              )}
              <div className="bb-member-card-footer">
                {submittedIds.has(scheme.id) ? (
                  <button
                    className="bb-member-btn bb-member-btn-sm"
                    disabled
                    style={{ opacity: 0.65, cursor: 'not-allowed' }}
                  >
                    Referral Request Submitted
                  </button>
                ) : (
                  <button
                    className="bb-member-btn bb-member-btn-primary bb-member-btn-sm"
                    onClick={() => handleReferral(scheme)}
                    disabled={submitting[scheme.id]}
                  >
                    {submitting[scheme.id] ? 'Submitting...' : 'Submit Application'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {toast && (
        <div className={`bb-member-toast${toast.isError ? ' error' : ''}`}>{toast.msg}</div>
      )}
    </div>
  );
}

// ── Age helper ─────────────────────────────────────────────────────────────────
const computeAge = (dob) => {
  if (!dob) return null;
  const today = new Date();
  const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

// ── Eligibility Check Tab ──────────────────────────────────────────────────────
function EligibilityCheck({ orgId, memberId }) {
  const [answers, setAnswers] = useState({
    children: '',
    childAges: [],
    income: '',
    employment: '',
    familyStatus: '',
    age: null,
    submitted: false,
  });
  const [schemes, setSchemes] = useState([]);
  const [loadingSchemes, setLoadingSchemes] = useState(false);
  const [memberAge, setMemberAge] = useState(null);

  useEffect(() => {
    if (!orgId || !memberId) return;
    getMember(orgId, memberId).then((m) => {
      if (m?.dateOfBirth) {
        const age = computeAge(m.dateOfBirth);
        setMemberAge(age);
        setAnswers((prev) => ({ ...prev, age }));
      }
    }).catch(() => {});
  }, [orgId, memberId]);

  const childAgeOptions = ['Under 6', '6–12', '13–17', '18+'];

  const toggleChildAge = (age) => {
    setAnswers((prev) => ({
      ...prev,
      childAges: prev.childAges.includes(age)
        ? prev.childAges.filter((a) => a !== age)
        : [...prev.childAges, age],
    }));
  };

  const handleCheck = async () => {
    setLoadingSchemes(true);
    try {
      const data = await getSchemes(orgId);
      setSchemes(data);
      setAnswers((prev) => ({ ...prev, submitted: true }));
    } catch (err) {
      console.error('Error loading schemes:', err);
    } finally {
      setLoadingSchemes(false);
    }
  };

  const isComplete = answers.children && answers.income && answers.employment && answers.familyStatus;

  return (
    <div>
      <h2 className="bb-member-section-title">Eligibility Check</h2>
      <p className="bb-member-section-sub">
        Answer a few questions to see which schemes you may qualify for.
      </p>

      <div className="bb-member-questionnaire">
        <p className="bb-member-q-title">Quick Eligibility Questionnaire</p>
        <p className="bb-member-q-subtitle">
          Your answers are used only to filter scheme suggestions and are not saved.
        </p>

        {/* Age — auto-filled from profile DOB; member can enter manually if not set */}
        <div className="bb-member-q-group">
          <label>Your Age</label>
          {memberAge !== null ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
              <span style={{
                display: 'inline-block', padding: '6px 14px',
                background: '#EFF6FF', border: '1px solid #BFDBFE',
                borderRadius: 6, fontSize: 14, fontWeight: 600, color: '#1D4ED8',
              }}>
                {memberAge} years old
              </span>
              <span style={{ fontSize: 12, color: '#9CA3AF' }}>Auto-filled from your profile</span>
            </div>
          ) : (
            <div style={{ marginTop: 6 }}>
              <input
                type="number"
                className="bb-input"
                min={0}
                max={120}
                placeholder="Enter your age"
                value={answers.age ?? ''}
                onChange={(e) => {
                  const val = e.target.value === '' ? null : parseInt(e.target.value);
                  setAnswers((prev) => ({ ...prev, age: val }));
                }}
                style={{ width: 140 }}
              />
              <span style={{ display: 'block', fontSize: 12, color: '#9CA3AF', marginTop: 4 }}>
                Add your Date of Birth in your profile to auto-fill this field.
              </span>
            </div>
          )}
        </div>

        {/* Q1: Number of children */}
        <div className="bb-member-q-group">
          <label>How many dependent children do you have?</label>
          <div className="bb-member-q-options">
            {['0', '1', '2', '3+'].map((opt) => (
              <label key={opt} className="bb-member-q-option">
                <input
                  type="radio"
                  name="children"
                  value={opt}
                  checked={answers.children === opt}
                  onChange={(e) => setAnswers({ ...answers, children: e.target.value })}
                />
                {opt}
              </label>
            ))}
          </div>
        </div>

        {/* Q2: Ages of children */}
        {answers.children && answers.children !== '0' && (
          <div className="bb-member-q-group">
            <label>What are the ages of your children? (select all that apply)</label>
            <div className="bb-member-q-options">
              {childAgeOptions.map((age) => (
                <label key={age} className="bb-member-q-option">
                  <input
                    type="checkbox"
                    checked={answers.childAges.includes(age)}
                    onChange={() => toggleChildAge(age)}
                  />
                  {age}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Q3: Income */}
        <div className="bb-member-q-group">
          <label>Monthly household income range</label>
          <div className="bb-member-q-options">
            {['Under $1,500', '$1,500 – $3,000', 'Over $3,000'].map((opt) => (
              <label key={opt} className="bb-member-q-option">
                <input
                  type="radio"
                  name="income"
                  value={opt}
                  checked={answers.income === opt}
                  onChange={(e) => setAnswers({ ...answers, income: e.target.value })}
                />
                {opt}
              </label>
            ))}
          </div>
        </div>

        {/* Q4: Employment */}
        <div className="bb-member-q-group">
          <label>Current employment status</label>
          <div className="bb-member-q-options">
            {['Employed', 'Unemployed', 'Part-time'].map((opt) => (
              <label key={opt} className="bb-member-q-option">
                <input
                  type="radio"
                  name="employment"
                  value={opt}
                  checked={answers.employment === opt}
                  onChange={(e) => setAnswers({ ...answers, employment: e.target.value })}
                />
                {opt}
              </label>
            ))}
          </div>
        </div>

        {/* Q5: Family status */}
        <div className="bb-member-q-group">
          <label>What is your family situation?</label>
          <div className="bb-member-q-options">
            {['Widowed', 'Divorced', 'Single Parent', 'Other / Not applicable'].map((opt) => (
              <label key={opt} className="bb-member-q-option">
                <input
                  type="radio"
                  name="familyStatus"
                  value={opt}
                  checked={answers.familyStatus === opt}
                  onChange={(e) => setAnswers({ ...answers, familyStatus: e.target.value })}
                />
                {opt}
              </label>
            ))}
          </div>
        </div>

        <button
          className="bb-member-btn bb-member-btn-primary"
          onClick={handleCheck}
          disabled={!isComplete || loadingSchemes}
          style={{ marginTop: 8 }}
        >
          {loadingSchemes ? 'Checking...' : 'Check My Eligibility'}
        </button>
      </div>

      {/* Results */}
      {answers.submitted && (
        <div style={{ marginTop: 28 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1E293B', marginBottom: 14 }}>
            Scheme Matches
          </h3>

          {schemes.length === 0 ? (
            <div className="bb-empty-state">
              <p>No schemes are currently available. Please check back later.</p>
            </div>
          ) : (() => {
            const qualifying = schemes.filter((s) => schemeMatches(s, answers) !== false);
            const nonQualifying = schemes.filter((s) => schemeMatches(s, answers) === false);
            return (
              <>
                {qualifying.length > 0 && (
                  <>
                    <p style={{ fontSize: 13, color: '#16A34A', fontWeight: 600, marginBottom: 10 }}>
                      {qualifying.length} scheme{qualifying.length > 1 ? 's' : ''} you may qualify for
                    </p>
                    <div className="bb-member-grid" style={{ marginBottom: 20 }}>
                      {qualifying.map((scheme) => (
                        <div key={scheme.id} className="bb-member-card">
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                            <div className="bb-member-card-title">{scheme.name}</div>
                            <span className="bb-member-badge bb-member-badge--green">Likely qualifying</span>
                          </div>
                          {scheme.benefitsSummary && (
                            <div className="bb-member-card-desc">{scheme.benefitsSummary}</div>
                          )}
                          {scheme.eligibility && (
                            <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>
                              <strong>Criteria:</strong> {eligDisplay(scheme.eligibility)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {qualifying.length === 0 && (
                  <div className="bb-member-info-box" style={{ marginBottom: 20 }}>
                    Based on your answers, no schemes appear to be an exact match right now. You can still browse all schemes or speak to a caseworker for personalised guidance.
                  </div>
                )}

                {nonQualifying.length > 0 && (
                  <>
                    <p style={{ fontSize: 13, color: '#6B7280', fontWeight: 600, marginBottom: 10 }}>
                      Other schemes (criteria not met)
                    </p>
                    <div className="bb-member-grid">
                      {nonQualifying.map((scheme) => (
                        <div key={scheme.id} className="bb-member-card" style={{ opacity: 0.5 }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                            <div className="bb-member-card-title">{scheme.name}</div>
                            <span className="bb-member-badge bb-member-badge--gray">May not qualify</span>
                          </div>
                          {scheme.eligibility && (
                            <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>
                              <strong>Criteria:</strong> {eligDisplay(scheme.eligibility)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            );
          })()}

          <p className="bb-member-privacy-note" style={{ marginTop: 16 }}>
            These are suggestions based on your answers. A caseworker will confirm your actual eligibility when you submit a referral.
          </p>
        </div>
      )}
    </div>
  );
}

// ── My Referrals Tab ───────────────────────────────────────────────────────────
function MyReferrals({ orgId, memberId, memberName }) {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewRef, setViewRef] = useState(null);
  const [refMessage, setRefMessage] = useState('');
  const [refMessageSending, setRefMessageSending] = useState(false);
  const [refFile, setRefFile] = useState(null);
  const [refFileUploading, setRefFileUploading] = useState(false);
  const [refError, setRefError] = useState('');

  const loadReferrals = useCallback(async () => {
    try {
      const data = await getMemberReferrals(orgId, memberId);
      setReferrals(data);
      return data;
    } catch (err) {
      console.error('Error loading referrals:', err);
      return [];
    }
  }, [orgId, memberId]);

  useEffect(() => {
    if (!memberId) return;
    setLoading(true);
    loadReferrals().finally(() => setLoading(false));
  }, [memberId, loadReferrals]);

  const handleSendMessage = async () => {
    if (!refMessage.trim()) return;
    setRefMessageSending(true);
    setRefError('');
    try {
      await addGovApplicationMessage(orgId, viewRef.id, memberId, memberName || memberId, 'member', refMessage.trim());
      const msg = { senderId: memberId, senderName: memberName || memberId, senderRole: 'member', text: refMessage.trim(), sentAt: new Date().toISOString() };
      setViewRef(prev => ({ ...prev, correspondence: [...(prev.correspondence || []), msg] }));
      setRefMessage('');
      loadReferrals();
    } catch (e) {
      setRefError(e.message || 'Failed to send message.');
    } finally {
      setRefMessageSending(false);
    }
  };

  const handleUploadDoc = async () => {
    if (!refFile) return;
    setRefFileUploading(true);
    setRefError('');
    try {
      const docMeta = await uploadReferralDocument('governmentProgrammesReferrals', orgId, viewRef.id, refFile, memberName || memberId);
      setViewRef(prev => ({ ...prev, submittedDocuments: [...(prev.submittedDocuments || []), docMeta] }));
      setRefFile(null);
      loadReferrals();
    } catch (e) {
      setRefError(e.message || 'Failed to upload document.');
    } finally {
      setRefFileUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="bb-empty-state">
        <div className="bb-loading-spinner"></div>
        <p>Loading your referrals...</p>
      </div>
    );
  }

  return (
    <div>
      {/* View Referral Modal */}
      {viewRef && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '32px 16px' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 640, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>Referral Details</h3>
              <button onClick={() => setViewRef(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6B7280' }}>✕</button>
            </div>
            {refError && <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 6, padding: '10px 14px', color: '#DC2626', fontSize: 13, marginBottom: 16 }}>{refError}</div>}

            {/* Details */}
            <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 16, marginBottom: 20, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              <div><div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', marginBottom: 2 }}>Scheme</div><div style={{ fontWeight: 600 }}>{viewRef.schemeName}</div></div>
              <div><div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', marginBottom: 2 }}>Status</div><div>{referralStatusBadge(viewRef.status)}</div></div>
              <div><div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', marginBottom: 2 }}>Submitted</div><div>{formatDate(viewRef.createdAt)}</div></div>
              <div><div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', marginBottom: 2 }}>Last Updated</div><div>{formatDate(viewRef.updatedAt)}</div></div>
              {viewRef.claimedByName && <div><div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', marginBottom: 2 }}>Caseworker</div><div>{viewRef.claimedByName}</div></div>}
            </div>

            {/* Correspondence */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>Messages</div>
              <div style={{ border: '1px solid #E5E7EB', borderRadius: 8, maxHeight: 240, overflowY: 'auto', padding: 12, marginBottom: 10, background: '#FAFAFA' }}>
                {(viewRef.correspondence || []).length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 13, padding: '16px 0' }}>No messages yet.</div>
                ) : (
                  [...(viewRef.correspondence || [])].sort((a, b) => new Date(a.sentAt) - new Date(b.sentAt)).map((msg, i) => (
                    <div key={i} style={{ marginBottom: 10, display: 'flex', flexDirection: msg.senderRole === 'member' ? 'row-reverse' : 'row', gap: 8 }}>
                      <div style={{ background: msg.senderRole === 'member' ? 'var(--org-primary, #3A7D44)' : '#F3F4F6', color: msg.senderRole === 'member' ? '#fff' : '#111827', padding: '8px 12px', borderRadius: 8, maxWidth: '75%', fontSize: 13 }}>
                        <div style={{ fontWeight: 600, fontSize: 11, marginBottom: 3, opacity: 0.85 }}>{msg.senderRole === 'member' ? 'You' : msg.senderName}</div>
                        <div>{msg.text}</div>
                        <div style={{ fontSize: 11, opacity: 0.7, marginTop: 3 }}>{msg.sentAt ? new Date(msg.sentAt).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : ''}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  className="bb-input"
                  style={{ flex: 1, fontSize: 14 }}
                  placeholder="Send a message to your caseworker…"
                  value={refMessage}
                  onChange={e => setRefMessage(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                />
                <button className="bb-member-btn bb-member-btn-primary bb-member-btn-sm" onClick={handleSendMessage} disabled={!refMessage.trim() || refMessageSending}>
                  {refMessageSending ? '…' : 'Send'}
                </button>
              </div>
            </div>

            {/* Documents */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>Documents</div>
              {(viewRef.submittedDocuments || []).length === 0 ? (
                <div style={{ color: '#9CA3AF', fontSize: 13, marginBottom: 10 }}>No documents uploaded yet.</div>
              ) : (
                <div style={{ marginBottom: 12 }}>
                  {(viewRef.submittedDocuments || []).map((docItem, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6, marginBottom: 6 }}>
                      <span style={{ fontSize: 18 }}>📄</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{docItem.name || 'Document'}</div>
                        {docItem.uploadedAt && <div style={{ fontSize: 11, color: '#9CA3AF' }}>Uploaded {formatDate(docItem.uploadedAt)}</div>}
                      </div>
                      {docItem.url && <a href={docItem.url} target="_blank" rel="noopener noreferrer" className="bb-member-btn bb-member-btn-sm" style={{ textDecoration: 'none' }}>View</a>}
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="file"
                  id="ref-doc-upload"
                  style={{ display: 'none' }}
                  onChange={e => setRefFile(e.target.files[0] || null)}
                />
                <label htmlFor="ref-doc-upload" className="bb-member-btn bb-member-btn-sm" style={{ cursor: 'pointer', border: '1px solid #D1D5DB' }}>
                  {refFile ? refFile.name : 'Choose File'}
                </label>
                {refFile && (
                  <button className="bb-member-btn bb-member-btn-primary bb-member-btn-sm" onClick={handleUploadDoc} disabled={refFileUploading}>
                    {refFileUploading ? 'Uploading…' : 'Upload'}
                  </button>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="bb-member-btn bb-member-btn-sm" style={{ border: '1px solid #D1D5DB' }} onClick={() => setViewRef(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      <h2 className="bb-member-section-title">My Referrals</h2>
      <p className="bb-member-section-sub">Track the status of your scheme referral requests.</p>

      {referrals.length === 0 ? (
        <div className="bb-empty-state">
          <p>You have not submitted any referral requests yet.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="bb-table">
            <thead>
              <tr>
                <th>Scheme</th>
                <th>Submitted</th>
                <th>Updated</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {referrals.map((r) => (
                <tr key={r.id}>
                  <td style={{ fontWeight: 500 }}>{r.schemeName}</td>
                  <td>{formatDate(r.createdAt)}</td>
                  <td>{formatDate(r.updatedAt)}</td>
                  <td>{referralStatusBadge(r.status)}</td>
                  <td>
                    <button
                      className="bb-member-btn bb-member-btn-sm"
                      style={{ border: '1px solid #D1D5DB' }}
                      onClick={() => { setViewRef(r); setRefError(''); setRefMessage(''); setRefFile(null); }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
function GovSchemesMember({ orgId, memberId, memberName, activeTab }) {
  return (
    <>
      {activeTab === 'Explore Schemes' && (
        <ExploreSchemes orgId={orgId} memberId={memberId} memberName={memberName} />
      )}
      {activeTab === 'Eligibility Check' && (
        <EligibilityCheck orgId={orgId} memberId={memberId} />
      )}
      {activeTab === 'My Referrals' && (
        <MyReferrals orgId={orgId} memberId={memberId} memberName={memberName} />
      )}
    </>
  );
}

export default GovSchemesMember;
